import React, { useEffect, useState } from 'react'
import { Input, Button, ProgressBarAnimated, InformationToken } from '@intelie/ui'

import i18n from 'live/services/i18n'
import TimeRange from 'live/components/ui/time-range'
import globalNotification from 'live/services/global-notification'

import TemplateFormRenderer from '../TemplateFormRenderer'
import { AdvancedAnnotation, Template } from '../../../types'
import { ViewAnnotationExpanded, AnnotationActions } from '../../styles'
import { getTemplate, deleteAnnotation } from '../../../services/template'
import { FormRenderer } from '../../../formBuilder/components/FormRenderer'
import PrettierJson from './PrettierJson'

interface AnnotationViewProps {
    event: AdvancedAnnotation
    mode: string
    callbackEdit: (event: AdvancedAnnotation, FormConfigs: FormConfigs) => void
    callbackDelete: (event: AdvancedAnnotation) => void
}

const AnnotationView: React.FC<AnnotationViewProps> = ({
    event,
    mode,
    callbackEdit,
    callbackDelete
}: AnnotationViewProps) => {
    const [template, setTemplate] = useState<Template>()
    const [loading, setLoading] = useState<boolean>(true)

    useEffect(() => {
        const fetchTemplate = async (): Promise<void> => {
            try {
                const data = (await getTemplate(event.templateId as string)) as Template
                setTemplate(data)
            } catch (error) {
                globalNotification.error(i18n(error.message), 5)
            }
            setLoading(false)
        }

        if (event) {
            fetchTemplate()
        }
    }, [event])

    const onDelete = (): void => {
        try {
            deleteAnnotation(event?.uid)
            globalNotification.success(i18n('Annotation deleted successfully'), 5)
            callbackDelete(event)
        } catch (error) {
            globalNotification.error(i18n(error.message), 5)
        }
    }

    return (
        <ViewAnnotationExpanded>
            {loading ? (
                <ProgressBarAnimated />
            ) : (
                <>
                    <TimeRange color={event?.__color} begin={event?.begin} end={event?.end} mode={mode} />
                    <AnnotationActions>
                        <Button
                            icon="edit"
                            variant="text"
                            disabled={!template}
                            onClick={() => callbackEdit(event, TemplateFormRenderer)}
                        >
                            {i18n('Edit')}
                        </Button>
                        <Button icon="trash-alt" variant="text" className="remove" onClick={onDelete}>
                            {i18n('Remove')}
                        </Button>
                    </AnnotationActions>
                    {template ? (
                        <FormRenderer
                            schema={JSON.parse(template.schema) as FormSchema}
                            data={event?.data}
                            period={{ begin: event.begin, end: event?.end }}
                            onChange={() => {}}
                            mode="readOnly"
                        />
                    ) : (
                        <>
                            <InformationToken type="info">
                                {i18n(
                                    "We couldn't find the template that renders this data. You can check the data down below."
                                )}
                            </InformationToken>
                            <PrettierJson json={event.data} />
                        </>
                    )}
                </>
            )}
        </ViewAnnotationExpanded>
    )
}

export default AnnotationView
